import React from 'react';
import PropTypes from 'prop-types';
import { Truck, ArrowRight } from 'lucide-react';
import Button from '../../Button';
import Price from '../../Price';
import './StickyFooter.css';

/**
 * StickyFooter — Configurator
 *
 * Sticky bottom bar for product configurator: total price, delivery info, and CTA.
 *
 * Desktop (≥ 1024px):
 *   [ Total 289,- | 🚚 3 WEEKS • FREE  Delivery to school ]    [ Add to Cart → ]
 *   White bg, top border, popup shadow, 80px height.
 *
 * Mobile (< 1024px):
 *   [ Total 289,- ]  [ Add to Cart → ]
 *   Floating bar with backdrop-blur, rounded, double shadow (StickyFooter).
 *   Delivery info hidden.
 *
 * Figma nodes:
 *   Desktop: 3803:17976
 *   Mobile:  3917:4172
 */

const StickyFooter = ({
  price = '289,-',
  priceLabel = 'Total',
  priceError = false,
  targetText = null,
  deliveryTime = '3 Weeks',
  deliveryPrice = 'Free',
  deliveryText = 'Delivery to your school',
  deliveryMessage = null,
  showDelivery = true,
  buttonLabel = 'Add to Cart',
  buttonVariant = 'alternative',
  buttonLoading = false,
  buttonSuccess = false,
  buttonDisabled = false,
  onButtonClick,
  className = '',
  ...props
}) => {
  return (
    <div className={`cfg-sticky-footer ${className}`.trim()} {...props}>
      <div className="cfg-sticky-footer__inner">
        {/* ---- Left: price + delivery ---- */}
        <div className="cfg-sticky-footer__left">
          {/* Total price block */}
          <div className="cfg-sticky-footer__total">
            <div className="cfg-sticky-footer__total-inner">
              <span className="cfg-sticky-footer__label typo-label">
                {priceLabel}
              </span>
              <Price
                type="large"
                price={price}
                className={`cfg-sticky-footer__price${priceError ? ' cfg-sticky-footer__price--error' : ''}`}
              />
              {targetText && (
                <span className="cfg-sticky-footer__target typo-body-sm">{targetText}</span>
              )}
            </div>
          </div>

          {/* Delivery info — desktop only */}
          {showDelivery && (
            <div className="cfg-sticky-footer__delivery">
              <div className="cfg-sticky-footer__delivery-content">
                <div className="cfg-sticky-footer__delivery-icon">
                  <Truck size={20} strokeWidth={1.5} />
                </div>
                <div className="cfg-sticky-footer__delivery-text">
                  {deliveryMessage ? (
                    <span className="cfg-sticky-footer__delivery-desc typo-body-sm">
                      {deliveryMessage}
                    </span>
                  ) : (
                    <>
                      <div className="cfg-sticky-footer__delivery-headline">
                        <span className="cfg-sticky-footer__delivery-time typo-h5">
                          {deliveryTime}
                        </span>
                        {deliveryPrice ? (
                          <>
                            <span className="cfg-sticky-footer__delivery-dot typo-label">•</span>
                            <span className="cfg-sticky-footer__delivery-cost typo-h5">
                              {deliveryPrice}
                            </span>
                          </>
                        ) : null}
                      </div>
                      {deliveryText ? (
                        <span className="cfg-sticky-footer__delivery-desc typo-body-sm">
                          {deliveryText}
                        </span>
                      ) : null}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ---- Right: CTA button ---- */}
        <div className="cfg-sticky-footer__right">
          <Button
            size="secondary"
            variant={buttonVariant}
            iconRight={buttonSuccess ? null : <ArrowRight size={20} />}
            loading={buttonLoading}
            success={buttonSuccess}
            disabled={buttonDisabled}
            onClick={onButtonClick}
            className="cfg-sticky-footer__btn"
          >
            {buttonLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

StickyFooter.propTypes = {
  /** Formatted price string, e.g. "289,-" */
  price: PropTypes.string,
  /** Label above the price */
  priceLabel: PropTypes.string,
  /** Render the price in red (e.g. total doesn't match a required target) */
  priceError: PropTypes.bool,
  /** Small text under the price (e.g. "Must match 330,-") */
  targetText: PropTypes.string,
  /** Delivery time text, e.g. "3 Weeks" */
  deliveryTime: PropTypes.string,
  /** Delivery price text, e.g. "Free" */
  deliveryPrice: PropTypes.string,
  /** Description below delivery headline */
  deliveryText: PropTypes.string,
  /** When set, replaces the time/price/desc with a single delivery message (vendor) */
  deliveryMessage: PropTypes.string,
  /** Show delivery info block (desktop only) */
  showDelivery: PropTypes.bool,
  /** CTA button label */
  buttonLabel: PropTypes.string,
  /** CTA button variant (passed to Button) */
  buttonVariant: PropTypes.string,
  /** CTA button loading state */
  buttonLoading: PropTypes.bool,
  /** CTA button disabled state */
  buttonDisabled: PropTypes.bool,
  /** CTA button click handler */
  onButtonClick: PropTypes.func,
  /** Additional CSS class */
  className: PropTypes.string,
};

export default StickyFooter;
